import moment from 'moment';

function isEmptyish(value: unknown): boolean {
  if (value == null) return true;
  if (value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

function normalizeArrayValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(String).sort();
  }
  if (isEmptyish(value)) {
    return [];
  }
  return [String(value)];
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    !(value instanceof Date)
  );
}

function normalizeBooleanLike(value: unknown): boolean | null {
  if (typeof value === 'boolean') {
    return value;
  }
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  return null;
}

/**
 * Normalizes date-like values to YYYY-MM-DD (matches DateEditor output).
 * Returns null for non-dates — plain numeric strings like windowStart "5" are excluded.
 */
function looksDateLikeString(value: string): boolean {
  return (
    /^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value) ||
    /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)
  );
}

function normalizeDateKey(value: unknown): string | null {
  if (value instanceof Date) {
    const m = moment(value);
    return m.isValid() ? m.format('YYYY-MM-DD') : null;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed === '' || /^\d+$/.test(trimmed) || !looksDateLikeString(trimmed)) {
    return null;
  }
  const strictIso = moment(trimmed, moment.ISO_8601, true);
  if (strictIso.isValid()) {
    return strictIso.format('YYYY-MM-DD');
  }
  const strictKnown = moment(trimmed, ['YYYY-MM-DD', 'MM/DD/YYYY'], true);
  return strictKnown.isValid() ? strictKnown.format('YYYY-MM-DD') : null;
}

/**
 * Compares an edited cell value to the original row value for dirty detection.
 * Normalizes empty text, arrays, numbers, booleans, dates, and plain objects.
 */
export function fieldValuesEqual(original: unknown, edited: unknown): boolean {
  if (isEmptyish(original) && isEmptyish(edited)) {
    return true;
  }

  if (Array.isArray(original) || Array.isArray(edited)) {
    const left = normalizeArrayValue(original);
    const right = normalizeArrayValue(edited);
    if (left.length !== right.length) return false;
    return left.every((value, index) => value === right[index]);
  }

  if (typeof original === 'number' || typeof edited === 'number') {
    return String(original) === String(edited);
  }

  const leftBoolean = normalizeBooleanLike(original);
  const rightBoolean = normalizeBooleanLike(edited);
  if (leftBoolean !== null && rightBoolean !== null) {
    return leftBoolean === rightBoolean;
  }

  const leftDate = normalizeDateKey(original);
  const rightDate = normalizeDateKey(edited);
  if (leftDate !== null && rightDate !== null) {
    return leftDate === rightDate;
  }

  if (isPlainObject(original) && isPlainObject(edited)) {
    const keys = new Set([...Object.keys(original), ...Object.keys(edited)]);
    for (const key of keys) {
      if (!fieldValuesEqual(original[key], edited[key])) {
        return false;
      }
    }
    return true;
  }

  return Object.is(original, edited);
}

/**
 * True when at least one key in the edit delta differs from the original row.
 * Read-only — does not mutate editingRows.
 */
export function rowHasSemanticDeltas(
  original: Record<string, unknown>,
  edits: Record<string, unknown> | undefined,
): boolean {
  if (edits == null || Object.keys(edits).length === 0) {
    return false;
  }

  return Object.entries(edits).some(
    ([field, value]) => !fieldValuesEqual(original[field], value),
  );
}

export function resolveRowKeyFromField(
  row: Record<string, unknown>,
  rowIndex: number,
  rowIdField: string,
): string {
  const raw = row[rowIdField];
  if (raw != null && raw !== '') {
    return String(raw);
  }
  return `__idx_${rowIndex}`;
}

function shallowRowContentEqual(
  left: Record<string, unknown>,
  right: Record<string, unknown>,
): boolean {
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    if (!fieldValuesEqual(left[key], right[key])) {
      return false;
    }
  }
  return true;
}

/**
 * True when two table row arrays represent the same logical data (ignoring array reference).
 * Rows are matched by rowIdField, not array index order.
 */
export function isSameTableData(
  previous: unknown[],
  next: unknown[],
  rowIdField: string,
): boolean {
  if (previous === next) {
    return true;
  }
  if (previous.length !== next.length) {
    return false;
  }

  for (let index = 0; index < next.length; index += 1) {
    const nextRow = next[index] as Record<string, unknown>;
    const rowKey = resolveRowKeyFromField(nextRow, index, rowIdField);
    const previousRow = previous.find((row, prevIndex) => {
      const record = row as Record<string, unknown>;
      return resolveRowKeyFromField(record, prevIndex, rowIdField) === rowKey;
    }) as Record<string, unknown> | undefined;

    if (previousRow == null || !shallowRowContentEqual(previousRow, nextRow)) {
      return false;
    }
  }

  return true;
}

/**
 * Whether external client data should be synced into table state.
 * Skips redundant SET_DATA while editing when content is unchanged (new array reference only).
 */
export function shouldSyncExternalTableData(params: {
  nextData: unknown[] | undefined;
  currentData: unknown[];
  editingRowCount: number;
  rowIdField: string;
}): boolean {
  const { nextData, currentData, editingRowCount, rowIdField } = params;
  if (nextData == null) {
    return false;
  }
  if (editingRowCount > 0 && nextData === currentData) {
    return false;
  }
  if (editingRowCount > 0 && isSameTableData(currentData, nextData, rowIdField)) {
    return false;
  }
  return true;
}
